import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useTracking } from '@/hooks/useTracking';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

import { extractWorkspaceId } from '../dashboard/useCalculateChurn';

/** Matches ASP.NET `SignalStatus` when enums serialize as JSON numbers (default). */
export const SIGNAL_STATUS = {
  New: 0,
  InProgress: 1,
  Resolved: 2,
  Dismissed: 3,
} as const;

async function putSignalStatus(
  workspaceId: string,
  signalId: string,
  status: number,
  token: string | undefined,
  notRelevantReason?: string | null,
) {
  const base = getBackendBaseUrl();
  const headers = backendRequestHeaders(token);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const trimmed = notRelevantReason?.trim();
  const body: { status: number; notRelevantReason?: string | null } = { status };
  if (trimmed) {
    body.notRelevantReason = trimmed;
  }

  const { data } = await axios.put<{ message: string }>(
    `${base}/workspaces/${workspaceId}/churn-signals/${signalId}/status`,
    body,
    { headers },
  );

  return data;
}

export function useUpdateSignalStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = extractWorkspaceId(workspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const bearer = resolveBearerToken(sessionJwt);

  return useMutation({
    mutationFn: ({
      signalId,
      status,
      notRelevantReason,
    }: {
      signalId: string;
      status: number;
      notRelevantReason?: string | null;
    }) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      return putSignalStatus(workspaceId, signalId, status, bearer || undefined, notRelevantReason);
    },
    onSuccess: async (_, { signalId, status }) => {
      trackEvent('signal_status_updated', {
        signal_id: signalId,
        status,
      });
      await queryClient.invalidateQueries([queryKeys.accountMetrics, workspaceId]);
    },
  });
}
